import { useLayoutEffect } from 'react'
import { scrollMainContentToTop } from '../../utils/scrollElementToScrollRootTop.js'
import styles from './recipe.module.css'

const INGREDIENT_ROWS = [1, 2, 3, 4, 5, 6, 7, 8]
const METHOD_ROWS = [1, 2, 3, 4]
const PAIRING_ROWS = [1, 2, 3]

/**
 * Loading skeleton for Recipe Detail — mirrors hero, pantry bar, and body columns.
 * Used while GET /saved-lists/:id is in flight (and as Suspense fallback).
 */
export default function RecipeDetailSkeleton() {
  // Cover the Suspense gap before RecipeDetail mounts so the user never sees the
  // previous tab's mid-page scroll while the lazy chunk loads.
  useLayoutEffect(() => {
    scrollMainContentToTop()
  }, [])

  return (
    <div
      className={`${styles.recipeDetailPage} ${styles.recipeDetailSkeleton}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">Loading recipe</span>

      <div className={styles.heroBand} aria-hidden="true">
        <div className="app-layout">
          <div className={styles.breadcrumbs}>
            <div className={`${styles.skeletonLine} ${styles.skeletonBreadcrumb}`} />
          </div>

          <div className={styles.heroGrid}>
            <div>
              <div className={`${styles.skeletonLine} ${styles.skeletonEyebrow}`} />
              <div className={`${styles.skeletonLine} ${styles.skeletonTitle}`} />
              <div className={`${styles.skeletonLine} ${styles.skeletonBlurb}`} />
              <div className={`${styles.skeletonLine} ${styles.skeletonBlurb} ${styles.skeletonBlurbShort}`} />

              <div className={styles.cookMetaRow}>
                <div className={`${styles.skeletonLine} ${styles.skeletonChip}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonChip} ${styles.skeletonChipNarrow}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonChip} ${styles.skeletonChipNarrow}`} />
              </div>

              <div className={styles.ctaRow}>
                <div className={`${styles.skeletonLine} ${styles.skeletonCta}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonCta} ${styles.skeletonCtaSecondary}`} />
              </div>
            </div>

            <div className={`${styles.skeletonBlock} ${styles.skeletonHeroMedia}`} />
          </div>
        </div>
      </div>

      <div className={styles.pantryBar} aria-hidden="true">
        <div className="app-layout">
          <div className={styles.pantryBarGrid}>
            <div>
              <div className={`${styles.skeletonLine} ${styles.skeletonPantryLabel}`} />
              <div className={`${styles.skeletonLine} ${styles.skeletonPantryText}`} />
            </div>
            <div>
              <div className={`${styles.skeletonLine} ${styles.skeletonPantryLabel}`} />
              <div className={`${styles.skeletonLine} ${styles.skeletonPantryText} ${styles.skeletonPantryTextShort}`} />
            </div>
            <div className={styles.pantryAction}>
              <div className={`${styles.skeletonLine} ${styles.skeletonPantryAction}`} />
            </div>
          </div>
        </div>
      </div>

      <div className={`${styles.recipeBody} app-layout`} aria-hidden="true">
        <div>
          <div className={styles.skeletonSectionDivider}>
            <div className={`${styles.skeletonLine} ${styles.skeletonSectionLabel}`} />
            <div className={styles.skeletonSectionLine} />
          </div>
          {INGREDIENT_ROWS.map((i) => (
            <div key={i} className={styles.skeletonIngredientRow}>
              <div className={`${styles.skeletonLine} ${styles.skeletonIngredientQty}`} />
              <div
                className={`${styles.skeletonLine} ${styles.skeletonIngredientName}${
                  i % 3 === 0 ? ` ${styles.skeletonIngredientNameShort}` : ''
                }`}
              />
            </div>
          ))}
        </div>

        <div>
          <div className={styles.skeletonSectionDivider}>
            <div className={`${styles.skeletonLine} ${styles.skeletonSectionLabel} ${styles.skeletonSectionLabelWide}`} />
            <div className={styles.skeletonSectionLine} />
          </div>
          {METHOD_ROWS.map((i) => (
            <div key={i} className={styles.skeletonMethodRow}>
              <div className={`${styles.skeletonBlock} ${styles.skeletonMethodNum}`} />
              <div className={styles.skeletonMethodCopy}>
                <div className={`${styles.skeletonLine} ${styles.skeletonMethodLine}`} />
                <div
                  className={`${styles.skeletonLine} ${styles.skeletonMethodLine}${
                    i % 2 === 0 ? ` ${styles.skeletonMethodLineShort}` : ''
                  }`}
                />
              </div>
            </div>
          ))}
        </div>

        <aside className={styles.skeletonAside}>
          <div className={`${styles.skeletonBlock} ${styles.skeletonSidebarAsk}`} />
          <div className={`${styles.skeletonBlock} ${styles.skeletonSidebarCard}`} />
          <div className={styles.skeletonSectionDivider}>
            <div className={`${styles.skeletonLine} ${styles.skeletonSectionLabel}`} />
            <div className={styles.skeletonSectionLine} />
          </div>
          {PAIRING_ROWS.map((i) => (
            <div key={i} className={styles.skeletonPairingRow}>
              <div className={`${styles.skeletonBlock} ${styles.skeletonPairingThumb}`} />
              <div className={styles.skeletonPairingInfo}>
                <div className={`${styles.skeletonLine} ${styles.skeletonPairingName}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonPairingMeta}`} />
              </div>
            </div>
          ))}
        </aside>
      </div>
    </div>
  )
}
